"use client";

import { useAppDispatch, useAppSelector } from "@/redux/hook";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import moment from "moment";
import { GetSMSThunk, ResendSMSThunk } from "@/redux/features/actions/sms";
import { Modal } from "antd";
import { ExclamationCircleFilled, EyeFilled } from "@ant-design/icons";
import { ColumnDef } from "@tanstack/react-table";
import { getDataType } from "@/components/table2/types";
import DataTable from "@/components/table2";
import Filters from "./filter";

export default function SMSListPage() {
  const dispatch = useAppDispatch();
  const { list, info } = useAppSelector((s) => s.sms);
  const [selectedSMS, setSelectedSMS] = useState<any>(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [smsToResend, setSmsToResend] = useState<string | null>(null);

  const truncateMessage = (message: string, length: number = 30) => {
    return message.length > length 
      ? `${message.substring(0, length)}...` 
      : message;
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorFn: (row) => row?.recipient,
        header: "Recipient",
        cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
      },
      {
        accessorFn: (row) => row?.message,
        header: "Message",
        cell: ({ row, getValue }) => {
          return (
            <div 
              className="flex items-center gap-2 cursor-pointer hover:text-primary"
              onClick={() => {
                setSelectedSMS(row.original);
                setIsDetailModalVisible(true);
              }}
            >
              <EyeFilled />
              <span title="Click to view full message">
                {truncateMessage(getValue() as string, 50)}
              </span>
            </div>
          );
        },
      },
      {
        accessorFn: (row) => row?.created_at,
        header: "Date Sent",
        cell: ({ getValue }) => moment(getValue() as string).format("LLL"),
      },
      {
        accessorFn: (row) => row?.id,
        header: "Actions",
        cell: ({ getValue }) => {
          return (
            <button
              onClick={() => setSmsToResend(getValue() as string)}
              className="bg-primary font-thin border-none px-6 hover:bg-primary-dark text-white text-xs py-2 rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Resend
            </button>
          );
        },
      },
    ],
    [],
  );

  const getData = useCallback(
    async ({ options, type }: Partial<getDataType> = {}) => {
      try {
        // Common params setup
        const baseParams = {
          ...options?.filters,
          pageNumber: (options?.pagination?.pageIndex || 0) + 1, // Convert to 1-based
        };

        if (type === "export") {
          // For exports, use total as pageSize to get all records
          const exportParams = {
            ...baseParams,
            pageSize: options?.pagination?.total || 1000, // Fallback if total not available
          };

          const exportResponse = await dispatch(
            GetSMSThunk(exportParams)
          ).unwrap();

          console.log("first",exportParams)
          return exportResponse.list || [];
        }

     
        const regularParams = {
          ...baseParams,
          pageSize: options?.pagination?.pageSize || 10,
        };

        const response = await dispatch(
          GetSMSThunk(regularParams)
        ).unwrap();


        return response.list ?? []
      } catch (error: any) {
        console.error("Error fetching SMS:", error);
        return type === "export" 
          ? [] 
          : {
              data: [],
              total: 0,
              pageCount: 0,
              pageIndex: 0,
              pageSize: 10,
            };
      }
    },
    [dispatch]
  );

  useLayoutEffect(() => {
    getData({});
  }, [getData]);

  const handleResend = useCallback(() => {
    if (smsToResend) {
      dispatch(ResendSMSThunk(smsToResend));
      setSmsToResend(null);
    }
  }, [dispatch, smsToResend]);

  return (
    <div className="p-4">
      <DataTable<any>
        title="SMS List"
        columns={columns}
        data={list || []}
        getData={getData}
        customPagination
        pagination={{
          totalPages: info?.lastPage,
          pageNumber: (info?.currentPage || 1) - 1,
          pageSize: info?.pageSize,
          total: info?.total,
        }}
        allowExport
          AsideNode={Filters}
      />

      <Modal
        title="SMS Details"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={
          <button
            onClick={() => setIsDetailModalVisible(false)}
            className="bg-primary text-white px-4 py-2 rounded"
          >
            Close
          </button>
        }
        width={700}
      >
        {selectedSMS && (
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500">Recipient</p>
                <p className="font-medium">{selectedSMS.recipient}</p>
              </div>
            
              <div>
                <p className="text-gray-500">Date Sent</p>
                <p className="font-medium">
                  {moment(selectedSMS.created_at).format("LLL")}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Token</p>
                <p className="font-medium">{selectedSMS.token}</p>
              </div>
            </div>
            <div>
              <p className="text-gray-500">Full Message</p>
              <div className="p-3 bg-gray-50 rounded-md mt-1 whitespace-pre-wrap">
                {selectedSMS.message}
              </div>
            </div>
            {selectedSMS.status_message && (
              <div>
                <p className="text-gray-500">Status Message</p>
                <p className="font-medium">{selectedSMS.status_message}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Resend Confirmation Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <ExclamationCircleFilled className="text-yellow-500" />
            <span>Confirm Resend</span>
          </div>
        }
        open={!!smsToResend}
        onOk={handleResend}
        onCancel={() => setSmsToResend(null)}
        okText="Yes, Resend"
        cancelText="Cancel"
        okButtonProps={{
          className: "!bg-primary !border-primary hover:!bg-primary",
        }}
      >
        <p>Are you sure you want to resend this SMS message?</p>
        <p className="text-gray-500 text-sm mt-2">
          This will attempt to send the message again to the recipient.
        </p>
      </Modal>
    </div>
  );
}